'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';
import FAQItem from './FAQItem';

interface FAQ {
  question: string;
  answer: string;
  category: string;
}

const FAQSection = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');

  const faqs: FAQ[] = [
    {
      question: "How does ParkPing protect my privacy?",
      answer: "ParkPing routes parking-related calls through its own system. Your personal phone number is never shared with the caller.",
      category: "Privacy & Security"
    },
    {
      question: "Do I need to install any app?",
      answer: "No app is required. Callers can scan the QR code or dial the ParkPing number using their phone.",
      category: "Getting Started"
    },
    {
      question: "What if the caller does not have a smartphone?",
      answer: "Each ParkPing card includes a contact number that can be dialed from any phone. The call is still routed through ParkPing.",
      category: "Getting Started"
    },
    {
      question: "How does the call connection work?",
      answer: "When someone contacts ParkPing using the card, the system connects the call to the vehicle owner without revealing personal contact details.",
      category: "Technical"
    },
    {
      question: "Can I use ParkPing anywhere in India?",
      answer: "ParkPing is designed to work across common residential, commercial, and public parking environments in India.",
      category: "Coverage"
    },
    {
      question: "What if someone contacts me unnecessarily?",
      answer: "You are always in control. You may choose to answer or ignore any call routed through ParkPing.",
      category: "Privacy & Security"
    },
    {
      question: "How much does ParkPing cost?",
      answer: "ParkPing cards are available with one-time pricing. There are no recurring subscription charges for standard usage.",
      category: "Pricing"
    },
    {
      question: "Can I order multiple cards for my family?",
      answer: "Yes. Multiple ParkPing cards can be used for different vehicles or family members.",
      category: "Getting Started"
    },
    {
      question: "What happens if I change my phone number?",
      answer: "Your linked phone number can be updated through ParkPing support if required.",
      category: "Technical"
    },
    {
      question: "Does the ParkPing card expire?",
      answer: "The physical card does not expire and remains usable as long as it is linked to your account.",
      category: "General"
    },
    {
      question: "Can organizations use ParkPing?",
      answer: "Yes. ParkPing is suitable for apartments, offices, hospitals, and other shared parking environments.",
      category: "Business Solutions"
    }
  ];

  const categories = ['All', ...Array.from(new Set(faqs.map(faq => faq.category)))];

  const filteredFAQs = faqs.filter(faq => {
    const matchesSearch = faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         faq.answer.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === 'All' || faq.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  return (
    <section className="py-20 bg-gray-50">
      <div className="container mx-auto px-4">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-12">
            <div className="inline-block mb-4 px-6 py-2 bg-[#42AF47]/10 rounded-full border border-[#42AF47]/20">
              <span className="text-sm font-semibold text-[#42AF47]">Common Questions</span>
            </div>
            <h2 className="text-3xl md:text-4xl font-heading font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Clear answers to common questions about ParkPing and how the system works.
            </p>
          </div>

          <div className="mb-8 space-y-4">
            <div className="relative">
              <Icon 
                name="MagnifyingGlassIcon" 
                size={20} 
                className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500"
              />
              <input
                type="text"
                placeholder="Search questions..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-4 py-4 bg-white border border-gray-200 rounded-xl text-gray-900 placeholder:text-gray-400 focus:outline-none focus:border-[#42AF47] focus:ring-2 focus:ring-[#42AF47]/30 transition-all duration-200"
              />
            </div>

            <div className="flex flex-wrap gap-2">
              {categories.map((category) => (
                <button
                  key={category}
                  onClick={() => setSelectedCategory(category)}
                  className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                    selectedCategory === category
                      ? 'bg-[#42AF47] text-white shadow-md'
                      : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>
          </div>

          <div className="space-y-4">
            {filteredFAQs.length > 0 ? (
              filteredFAQs.map((faq, index) => (
                <FAQItem key={index} faq={faq} />
              ))
            ) : (
              <div className="text-center py-12">
                <Icon name="MagnifyingGlassIcon" size={48} className="text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No questions found matching your search.</p>
              </div>
            )}
          </div>

          <div className="mt-12 bg-gradient-to-br from-[#42AF47] to-[#2e8b3f] rounded-2xl p-8 text-center shadow-xl">
            <Icon name="ChatBubbleLeftRightIcon" size={48} className="text-white mx-auto mb-4" />
            <h3 className="text-2xl font-heading font-bold text-white mb-3">
              Still Have Questions?
            </h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              If you need further assistance, our support team is available to help with any questions related to ParkPing.
            </p>
            <a
              href="/support-hub"
              className="inline-flex items-center gap-2 px-6 py-3 bg-white text-[#42AF47] font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300"
            >
              Contact Support
              <Icon name="ArrowRightIcon" size={20} className="text-[#42AF47]" />
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQSection;